import { useState, useLayoutEffect, useRef, useCallback } from "react";
import styles from "./index.module.css";
import { classNames } from "../../utils";
import { getInviteUserList } from "../../request/server";

type Data = {
  phone: string;
  amount: string;
};

const nextFrame = (callback: () => void) => {
  let ref;
  ref = requestAnimationFrame(() => {
    ref = requestAnimationFrame(callback);
  });
  return ref;
};

const renderText = (data: { phone: string; amount: string }) => {
  return `用户${data.phone}累计已获得${data.amount}元佣金`;
};

export const VerticalScroll = () => {
  const [text, setText] = useState({
    curr: "",
    next: "",
  });
  const [status, setStatus] = useState<"stop" | "start" | "enter" | "leave">(
    "stop"
  );
  const timer = useRef<any>(-1);
  const record = useRef<{
    index: number;
    list: Data[];
  }>({
    index: -1,
    list: [],
  });
  const clear = useCallback(() => {
    if (timer.current !== -1) {
      clearTimeout(timer.current);
      cancelAnimationFrame(timer.current);
      timer.current = -1;
    }
  }, []);
  const updateText = useCallback(() => {
    const list = record.current.list;
    const last = list.length - 1;
    record.current.list = list;
    clear();
    let curr = record.current.index;
    curr += 1;
    if (curr > last) {
      curr = 0;
    }
    const newText = {
      curr: renderText(list[curr]),
      next: renderText(list[curr + 1 > last ? 0 : curr + 1]),
    };
    record.current.index = curr;
    setText(newText);
  }, [clear]);
  useLayoutEffect(() => {
    getInviteUserList().then((list) => {
      if (list.length > 0) {
        const last = list.length - 1;
        record.current.list = list;
        record.current.index = -1;
        clear();
        let curr = record.current.index + 1;
        if (curr > last) {
          curr = 0;
        }
        record.current.index = curr;
        const newText = {
          curr: renderText(list[curr]),
          next: renderText(list[curr + 1 > last ? 0 : curr + 1]),
        };
        setStatus("stop");
        setText(newText);
        setTimeout(() => {
          setStatus("start");
        }, 2200);
      } else {
        record.current.index = -1;
        record.current.list = [];
        setText({
          curr: "",
          next: "",
        });
      }
    });
    return clear;
  }, [clear]);
  useLayoutEffect(() => {
    if (status === "start") {
      setStatus("enter");
    } else if (status === "enter") {
      clear();
      timer.current = nextFrame(() => {
        setStatus("leave");
      });
    } else if (status === "leave") {
      clear();
      timer.current = setTimeout(() => {
        setStatus("stop");
        updateText();
        timer.current = setTimeout(() => {
          setStatus("start");
        }, 2200);
      }, 800);
    }
  }, [status, clear, updateText]);
  return (
    <div className={styles.box}>
      <div
        className={classNames(
          styles.slide,
          status === "stop" && styles.slide_curr + " " + styles.stop,
          status === "start" && styles.slide_curr,
          status === "enter" && styles.slide_prev,
          status === "leave" && styles.slide_prev
        )}
      >
        {text.curr}
      </div>
      <div
        className={classNames(
          styles.slide,
          status === "stop" && styles.slide_next + " " + styles.stop,
          status === "start" && styles.slide_next,
          status === "enter" && styles.slide_curr,
          status === "leave" && styles.slide_curr
        )}
      >
        {text.next}
      </div>
    </div>
  );
};

const supportResizeObserver = typeof window.ResizeObserver !== "undefined";
const listenerOnResize = (el: Element, resize: (width: number) => void) => {
  if (supportResizeObserver) {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentBoxSize[0]) {
          resize(Math.ceil(entry.contentBoxSize[0].inlineSize));
        }
      }
    });
    resizeObserver.observe(el);
    return () => {
      resizeObserver.unobserve(el);
      resizeObserver.disconnect();
    };
  }
  const onresize = () => {
    resize(el.clientWidth);
  };
  window.addEventListener("resize", onresize);
  return () => {
    window.removeEventListener("resize", onresize);
  };
};

export const HorizontalScroll = () => {
  const box = useRef<any>();
  const [update, forceUpdata] = useState(0);
  const listCache = useRef<{ left: number; width: number }[]>([]);
  const [list, setList] = useState<
    ({
      key: string;
      left: number;
      width: number;
    } & Data)[]
  >([]);
  useLayoutEffect(() => {
    if (box.current) {
      const width = box.current.clientWidth;
      getInviteUserList().then((list) => {
        if (list.length > 0) {
          setList(
            list.map((d, i) => ({
              ...d,
              key: i + "",
              left: width,
              width: 0,
            }))
          );
          listCache.current = list.map((v) => ({ left: width, width: 0 }));
          forceUpdata((x) => x + 1);
        } else {
          setList([]);
          listCache.current = [];
          forceUpdata(0);
        }
      });
    }
  }, []);
  useLayoutEffect(() => {
    if (box.current && update) {
      const distance = 2;
      let rafId = -1;
      const cancelRaf = () => {
        if (rafId !== -1) {
          cancelAnimationFrame(rafId);
          rafId = -1;
        }
      };
      const scroll = () => {
        if (listCache.current.length > 0) {
          listCache.current.forEach((cache) => {
            if (cache.left < width || space > 0) {
              cache.left -= distance;
              if (cache.width + cache.left <= 0) {
                cache.left = width;
              }
              space = width - cache.left - cache.width;
            }
          });
          setList((list) => {
            return list.map((v, i) => {
              const cache = listCache.current[i];
              return {
                ...v,
                left: cache ? cache.left : width,
              };
            });
          });
          cancelRaf();
          rafId = requestAnimationFrame(scroll);
        }
      };
      let width = box.current.clientWidth;
      let space = width;
      const onresize = (nextWidth: number) => {
        const diff = nextWidth - width;
        width = nextWidth;
        listCache.current.forEach((cache) => {
          cache.left += diff;
        });
      };
      const children = box.current.children;
      for (let i = 0; i < children.length; i++) {
        listCache.current[i].width = children[i].clientWidth + 50;
      }
      scroll();
      const unListener = listenerOnResize(box.current, onresize);
      return () => {
        cancelRaf();
        unListener();
      };
    }
  }, [update]);
  return (
    <div className={styles.container} ref={box}>
      {list.map((v) => (
        <div
          className={styles.item}
          key={v.key}
          style={{
            transform: `translate3D(${v.left}px, 0, 0)`,
          }}
        >
          {renderText(v)}
        </div>
      ))}
    </div>
  );
};

const InviteScroll = HorizontalScroll;
export default InviteScroll;
